import { useState, useEffect } from "react";
import {
    Box,
    Button,
    LinearProgress,
    Typography,
} from "@mui/material";
import { CloudUpload } from "@mui/icons-material";
import { uploadFileAPI } from "../service/fileService";
import { toast } from "react-toastify";

interface DragAndDropUploadWithFolderProps {
    spaceId: string;
    onUploadSuccess: () => void; // Callback to refresh folder list
    selectedFolder?: number | null;
}

const DragAndDropUploadWithFolder: React.FC<DragAndDropUploadWithFolderProps> = ({
    spaceId,
    onUploadSuccess,
    selectedFolder
}) => {
    const [dragging, setDragging] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    // const [selectedFolderId] = useState<number>(selectedFolder ?? 0); // Hidden & defaults to 0
    const [fileName, setFileName] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        setDragging(true);
    };

    const handleDragLeave = () => setDragging(false);

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        setDragging(false);
        const files = Array.from(event.dataTransfer.files);

        if (files.length > 0) {
            validateAndUpload(files[0]);
        }
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files ? Array.from(event.target.files) : [];
        if (files.length > 0) {
            validateAndUpload(files[0]);
        }
    };

    const validateAndUpload = (file: File) => {
        const allowedTypes = [
            "image/jpeg", "image/png",
            "application/pdf",
            "application/vnd.ms-powerpoint", "application/vnd.openxmlformats-officedocument.presentationml.presentation",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // DOC, DOCX
            "video/mp4", "video/quicktime" // MP4, MOV
        ];
        const maxSize = 50 * 1024 * 1024; // 50MB

        if (!allowedTypes.includes(file.type)) {
            setError("Invalid file type. Only PNG, JPEG, PPT, PPTX, XLSX, PDF, DOC, DOCX, MP4, or MOV files are allowed.");
            return;
        }

        if (file.size > maxSize) {
            setError("File size exceeds 50MB limit.");
            return;
        }

        setError(null);
        setFileName(file.name);
        setUploadProgress(0);
        uploadFile(file);
    };


    const uploadFile = async (file: File) => {
        try {
            let folderId = null;
            if (selectedFolder) {
                folderId = String(selectedFolder) || null;
            }
            // const response = await uploadFileAdmin(file, folderId, userId);

            const response = await uploadFileAPI(file, folderId, spaceId);

            if (response?.status === "success") {
                toast.success("File uploaded successfully!");
                setUploadProgress(100);
                onUploadSuccess();

                setTimeout(() => {
                    setUploadProgress(0);
                    setFileName(null);
                    setError(null);
                }, 1500);
            } else {
                setError(response?.message || "Upload failed. Please try again.");
                toast.error(response?.message || "Upload failed!");
            }
        } catch (error) {
            console.error("Upload Error:", error);
            setError("An error occurred while uploading.");
            toast.error("An error occurred while uploading.");
        }
    };

    return (
        <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
                border: `2px dashed ${dragging ? "#632DE9" : "#bdbdbd"}`,
                borderRadius: 2,
                textAlign: "center",
                padding: 3,
                backgroundColor: dragging ? "#f0f5ff" : "transparent",
            }}
        >
            <CloudUpload sx={{ fontSize: 48, color: "#632DE9" }} />
            <Typography variant="body1" sx={{ mt: 1 }}>
                Drag and drop a file here or
            </Typography>
            <Button variant="contained" component="label" sx={{ mt: 1 }}>
                Select File
                <input type="file" hidden onChange={handleFileSelect} />
            </Button>

            {error && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {error}
                </Typography>
            )}

            {fileName && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">{fileName}</Typography>
                    <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 1 }} />
                </Box>
            )}
        </Box>
    );
};

export default DragAndDropUploadWithFolder;
